/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
Autocomplete,
Box,
Button,
Grid,
Stack,
TextField,
Typography,
} from '@mui/material';
import formConfig from './formConfig';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { docInfo, formConfigField } from '../../utils/types';
import AutocompleteField from './AutoCompleteField';
import { formatName, formatPhoneNumber } from '../../utils/utilFuntions';
import useSyncedMedSelections from "./hooks/useSyncMedSelection.ts";

function ForthStep({
numDocs,
setNumDocs,
values,
setValues,
setValidStep,
}: {
numDocs: number;
setNumDocs: React.Dispatch<React.SetStateAction<number>>;
values: any;
setValues: React.Dispatch<React.SetStateAction<any>>;
setValidStep: React.Dispatch<React.SetStateAction<boolean>>;
}) {
const [errors, setErrors] = useState<{ [key: string]: string }>({});
const availableMedsforOrder = Array.from(
values.orders,
(order: any) => order.medicationName
).filter((medName: string) => medName !== '') as string[];

const [selectedMeds, setSelectedMeds] = useState<string[][]>(() =>
values.doctors.map(() => [])
);


// Check if all medications are selected for all doctors
const checkAllMedsSelected = useCallback(
    (allSelectedMeds: string[]) => {
        if (allSelectedMeds.length !== availableMedsforOrder.length) {
            for (let i = 0; i < numDocs; i++) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    ['prescribedMed' + i]: 'Please select all prescribed medications',
                }));
            }
        } else {
            for (let i = 0; i < numDocs; i++) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    ['prescribedMed' + i]: '',
                }));
            }
        }
    },
    [availableMedsforOrder.length, numDocs]
);

const { handleAutocompleteChange, syncFromOrders } = useSyncedMedSelections({
doctors: values.doctors,
orders: values.orders,
setValues,
checkAllMedsSelected,
});
// Centralized validation function
const validateFields = useCallback(
(name: string, value: string, object: docInfo) => {
let error = '';
if (name === 'phone' && !/^\(\d{3}\) \d{3} - \d{4}$/.test(value)) {
error = 'Invalid phone number';
} else if (name === 'fax' && !/^\(\d{3}\) \d{3} - \d{4}$/.test(value)) {
error = 'Invalid fax number';
} else if (name === 'faxValidate' && value !== object.fax) {
error = 'Fax number does not match';
} else if (name === 'zip' && !/^\d{5}$/.test(value)) {
error = 'Invalid Zip Code';
} else if (name === 'address' && value === '') {
error = 'Invalid Address';
} else if (
formConfig[3].fields.find((field) => field.name === name)?.required
) {
if (value === '') {
error = 'This field is required';
}
}
return error;
},
[]
);

const handleChangeWithValidation = (
event: React.ChangeEvent<HTMLInputElement>,
index: number
) => {
const fieldName = event.target.name;
const fieldValue = event.target.value;

// Perform validation
const error = validateFields(fieldName, fieldValue, values.doctors[index]);
setErrors((prevErrors) => ({
...prevErrors,
[fieldName + index]: error,
}));
if (
fieldName === 'fname' ||
fieldName === 'mname' ||
fieldName === 'lname'
) {
let name = formatName(fieldValue);
if (fieldName === 'mname') {
name = (name as string).charAt(0).toUpperCase();
}
setValues((prevValues: any) => ({
...prevValues,
doctors: prevValues.doctors.map((doc: docInfo, docIndex: number) =>
docIndex === index
? {
...doc,
[fieldName]: name,
}
: doc
),
}));
return;
}
// Update the specific doctor's field while preserving other values
setValues((prevValues: any) => ({
...prevValues,
doctors: prevValues.doctors.map((doc: docInfo, docIndex: number) =>
docIndex === index
? {
...doc, // Preserve other fields
[fieldName]: fieldValue, // Update the specific field
}
: doc
),
}));
};




// Format ZIP code
function formatZip(value: string) {
return value.replace(/\D/g, '').substring(0, 5);
}

// Filter options for medications excluding already selected ones
const getFilteredOptions = (index: number) => {
const otherSelectedMeds = selectedMeds.flatMap((meds, idx) =>
idx !== index ? meds : []
);
return availableMedsforOrder.filter(
(option) => !otherSelectedMeds.includes(option)
);
};

// Validate if all required fields are filled
const areAllRequiredFieldsFilled = useCallback(
(doctors: docInfo[]) => {
const requiredFields = [
'fname',
'lname',
'facility',
'phone',
'fullAddress',
'zip',
'fax',
'faxValidate',
];
let valid = true;

doctors.forEach((doctor: docInfo) => {
const areDoctorFieldsFilled = requiredFields.every(
(field) => !!doctor[field as keyof docInfo]
);
const docName =
doctor.mname === ''
? `${doctor.fname} ${doctor.lname}`
: `${doctor.fname} ${doctor.mname} ${doctor.lname}`;
const areOrdersFilled = values.orders
.filter((order: any) => order.medicationName !== '')
.some((order: any) => order.doctorName === docName);

if (!areDoctorFieldsFilled || !areOrdersFilled) {
valid = false;
}
});
const hasErrors = Object.values(errors).some((err) => err !== '');
return valid && !hasErrors;
},
[errors, values.orders]
);

// Run validation whenever doctors or errors change
useEffect(() => {
const isValid = areAllRequiredFieldsFilled(values.doctors);
setValidStep(isValid);
}, [areAllRequiredFieldsFilled, setValidStep, values.doctors]);
//fill medications selection from storage if available
// useEffect(() => {
// const docNames = values.doctors.map((doc: any) =>
// doc.mname
// ? `${doc.fname} ${doc.mname} ${doc.lname}`
// : `${doc.fname} ${doc.lname}`
// );
// if (docNames.every((name: string) => name === ' ')) return;
//
// const groupedMeds: string[][] = docNames.map((name: string) =>
// values.orders
// .filter((order: any) => order.doctorName === name)
// .map((order: any) => order.medicationName)
// );
// // If all groupedMeds arrays are empty, skip setup
// if (groupedMeds.every((arr) => arr.length === 0)) {
// return;
// }
// for (let i = 0; i < docNames.length; i++) {
// handleAutocompleteChange(i, groupedMeds[i]);
// }
// }, [values.orders, values.doctors, handleAutocompleteChange]);

useEffect(() => {
    syncFromOrders(setSelectedMeds);
}, [syncFromOrders]);

useEffect(() => {
setSelectedMeds((prev) => {
const newLength = values.doctors.length;
const updated = [...prev];

while (updated.length < newLength) {
updated.push([]);
}

return updated.slice(0, newLength);
});
}, [values.doctors.length]);

return (
<>
<Stack>
<Typography variant="h2" textAlign={'center'}>
Next, we need the contact information for your prescribing healthcare
provider so we can coordinate your care.
</Typography>
<Typography variant="h3" textAlign={'center'}>
We handle the details so you don't have to worry. Please provide the
correct Fax Number, it's the only communication channel with them
</Typography>
</Stack>
{numDocs > 0 &&
Array.from({ length: numDocs }, (_, index) => (
<React.Fragment key={index}>
<Box className="tprx-card">
<Grid container spacing={2}>
{formConfig[3].fields.map((field: formConfigField) => (
  <Grid
    item
    xs={12}
    sm={field.name === 'prescribedMed' ? 12 : 4}
    key={field.name}>
    {(() => {
      switch (field.type) {
        case 'select':
          return (
            <Autocomplete
              options={getFilteredOptions(index)}
              value={Array.isArray(selectedMeds[index]) ? selectedMeds[index] : []}
              onChange={(_e, value) => {
                  handleAutocompleteChange(index, value, selectedMeds, setSelectedMeds);
              }}
              multiple
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  name={field.name}
                  label={field.label}
                  variant="outlined"
                  error={!!errors[field.name + index]}
                  helperText={errors[field.name + index]}
                />
              )}
            />
          );
        case 'address':
          return (
            <Grid item xs={12} sm={12} key={field.name}>
              <AutocompleteField
                isRequired={true}
                label={field.label}
                name={field.name}
                value={
                  values.doctors[index]['fullAddress'] || ''
                }
                onChange={(addressValues) => {
                  Object.keys(addressValues).forEach((name) => {
                    const value =
                      addressValues[name as keyof docInfo];
                    handleChangeWithValidation(
                      {
                        target: { name, value },
                      } as unknown as React.ChangeEvent<HTMLInputElement>,
                      index
                    );
                  });
                }}
                errors={errors['fullAddress' + index]}
                setErrors={setErrors}
                errorObjectName={'fullAddress' + index}
              />
            </Grid>
          );

        default:
          return (
            <TextField
              error={!!errors[field.name + index]}
              helperText={errors[field.name + index]}
              required={field.required}
              label={field.label}
              name={field.name}
              type={field.type}
              value={values.doctors[index][field.name] || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (
                  field.name === 'phone' ||
                  field.name === 'fax' ||
                  field.name === 'faxValidate'
                ) {
                  formatPhoneNumber(e);
                } else if (field.name === 'zip') {
                  e.target.value = formatZip(e.target.value);
                }
                handleChangeWithValidation(e, index);
              }}
              margin="normal"
              fullWidth
              inputProps={
                field.name === 'ext'
                  ? {
                      min: 0,
                      step: 1,
                    }
                  : {}
              }
            />
          );
      }
    })()}
  </Grid>
))}
{index >= 1 && (
  <Grid item xs={12}>
    <Stack
      direction="row"
      spacing={2}
      justifyContent={'flex-end'}>
      <Button
        onClick={() => {
          setNumDocs(numDocs - 1);
          values.doctors.splice(index, 1);
          setValidStep(
            areAllRequiredFieldsFilled(values.doctors)
          );
        }}
        variant="contained">
        Remove
      </Button>
    </Stack>
  </Grid>
)}
</Grid>
</Box>
</React.Fragment>
))}
<Stack direction="row" spacing={2}>
<Button
onClick={() => {
setNumDocs((prev) => prev + 1);
setValues((prevValues: any) => ({
    ...prevValues,
    doctors: [
        ...prevValues.doctors,
        {
            fname: '',
            mname: '',
            lname: '',
            phone: '',
            ext: '',
            fax: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            country: '',
            facility: '',
            suite: '',
        },
    ],
}));
setSelectedMeds((prev) => [...prev, []]); // 🔧 Fix added here
setValidStep(false);
}}
variant="outlined">
I have another healthcare provider
</Button>
</Stack>
</>
);
}

export default ForthStep;
